import { upgradeFreePlanModalOpen } from '@/components/Modals/UpgradeFreePlanModal';
import { persistentClipsDns } from '@/environment';
import { useNavigateToAdvancedEditor } from '@/hooks/navigation';
import { useNavigateToAgentsEditor } from '@/hooks/navigation/useNavigateToAgentsEditor';
import { useFreeVideos } from '@/hooks/useFreeVideos';
import { ContentBar } from '@/pages/home/components/Content/ContentBar';
import { Tutorial, TutorialTile } from '@/pages/home/components/TutorialTile';
import { useAnalytics } from '@/services/analytics';
import { useFlag } from '@/services/featureFlags';
import { useTemplateGalleryModal } from '@/state/template-gallery/useTemplateGalleryModal';
import { useMemo } from 'react';
import { useSetRecoilState } from 'recoil';

interface TutorialConfig {
    id: string;
    title: string;
    url: string;
    duration: number;
    buttonType?: 'video' | 'agent';
}

const TUTORIALS_CONFIG: TutorialConfig[] = [
    {
        id: 'tutorial-welcome',
        title: 'Welcome to D-ID',
        url: encodeURI(`${persistentClipsDns}/D_ID_welcome_video.mp4`),
        duration: 75,
    },
    {
        id: 'tutorial-how-to-upload-an-image-for-your-avatar',
        title: 'How to upload an image for your avatar',
        url: encodeURI(`${persistentClipsDns}/LU_S_upload_image.mp4`),
        duration: 33,
        buttonType: 'video',
    },
    {
        id: 'tutorial-how-to-select-a-voice-for-your-avatar',
        title: 'How to select a voice for your avatar',
        url: encodeURI(`${persistentClipsDns}/LU_S_voice_selection.mp4`),
        duration: 28,
        buttonType: 'video',
    },
    {
        id: 'tutorial-studio-overview',
        title: 'Studio Overview',
        url: encodeURI(`${persistentClipsDns}/D_ID_studio_overview.mp4`),
        duration: 148,
        buttonType: 'video',
    },
    {
        id: 'tutorial-how-to-prompt-your-agent',
        title: 'How to prompt your agent',
        url: encodeURI(`${persistentClipsDns}/LU_A_agent_prompts_v2.mp4`),
        duration: 120,
        buttonType: 'agent',
    },
    {
        id: 'tutorial-how-to-clone-voice',
        title: 'How to clone your voice',
        url: encodeURI(`${persistentClipsDns}/D_ID_voice_cloning.mp4`),
        duration: 94,
    },
    {
        id: 'tutorial-how-to-set-up-your-agents-knowledge-base',
        title: "How to set up your agent's knowledge base",
        url: encodeURI(`${persistentClipsDns}/LU_A_how_to_create_datasets_for_agents.mp4`),
        duration: 136,
        buttonType: 'agent',
    },
    {
        id: 'tutorial-how-to-translate-video',
        title: 'How to translate your video',
        url: encodeURI(`${persistentClipsDns}/D_ID_how_to_translate_your_video.mp4`),
        duration: 63,
    },
];

export const Tutorials = () => {
    const mixpanel = useAnalytics();
    const { navigateToAdvancedEditor } = useNavigateToAdvancedEditor();
    const { navigateToAgentEditor } = useNavigateToAgentsEditor();
    const { outOfFreeVideos } = useFreeVideos();
    const setUpgradeFreePlanModalOpen = useSetRecoilState(upgradeFreePlanModalOpen);
    const { open } = useTemplateGalleryModal();
    const isTemplateGalleryEnabled = useFlag('template_gallery_v2', false);

    const createAgentButton = useMemo(
        () => ({
            label: 'Create an Agent',
            onClick: async () => {
                mixpanel?.track('workspace-cta', { event: 'click', id: 'create-agents', section: 'agents', from: 'tutorial-video' });
                navigateToAgentEditor({ mixPanelFrom: 'tutorial-video', from: 'Home' });
            },
        }),
        [mixpanel, navigateToAgentEditor]
    );

    const createVideoButton = useMemo(
        () => ({
            label: 'Create a video',
            onClick: async () => {
                mixpanel?.track('workspace-cta', { event: 'click', id: 'create-studio', section: 'videos', from: 'tutorial-video' });
                if (outOfFreeVideos) {
                    setUpgradeFreePlanModalOpen(true);
                    return;
                }

                if (isTemplateGalleryEnabled) {
                    open();
                    return;
                }
                await navigateToAdvancedEditor({ options: { from: 'Home' }, mixPanelFrom: 'tutorial-video' });
            },
        }),
        [mixpanel, navigateToAdvancedEditor, outOfFreeVideos, setUpgradeFreePlanModalOpen, isTemplateGalleryEnabled, open]
    );

    const tutorials = useMemo(
        () =>
            TUTORIALS_CONFIG.map(
                (config): Tutorial => ({
                    id: config.id,
                    title: config.title,
                    url: config.url,
                    duration: config.duration,
                    ...(config.buttonType === 'video' && { button: createVideoButton }),
                    ...(config.buttonType === 'agent' && { button: createAgentButton }),
                })
            ),
        [createVideoButton, createAgentButton]
    );

    return (
        <ContentBar id="tutorials-section" title="Tutorials">
            <>
                {tutorials.map(tutorial => (
                    <TutorialTile key={tutorial.id} tutorial={tutorial} />
                ))}
            </>
        </ContentBar>
    );
};
